import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { investmentApi, psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Landmark, ArrowLeft, Plus, Trash2, Link2, ChevronDown, ChevronRight, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

export default function InvestmentProgramDetail() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('investment_program');
  const [rollup, setRollup] = useState(null);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wbsList, setWbsList] = useState([]);

  const [posOpen, setPosOpen] = useState(false);
  const [posForm, setPosForm] = useState({ name: '', code: '', parent_position_id: 'none', planned_budget: 0 });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm, setAssignForm] = useState({ wbs_element_id: '', assigned_budget: 0 });
  const [expanded, setExpanded] = useState({});
  const [assignmentsByPos, setAssignmentsByPos] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        investmentApi.getRollup(programId),
        investmentApi.getProgram(programId),
      ]);
      setRollup(r.data);
      setProgram(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load program');
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { psAPI.getWBSElements().then((r) => setWbsList(r.data || [])).catch(() => {}); }, []);

  const money = (v) => `${rollup?.currency || ''} ${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim();

  const handleAddPosition = async () => {
    if (!posForm.name.trim() || !posForm.code.trim()) { toast.error('Name and code are required'); return; }
    try {
      await investmentApi.createPosition(programId, {
        name: posForm.name,
        code: posForm.code.toUpperCase(),
        parent_position_id: posForm.parent_position_id === 'none' ? null : posForm.parent_position_id,
        planned_budget: parseFloat(posForm.planned_budget) || 0,
      });
      toast.success('Position added');
      setPosOpen(false);
      setPosForm({ name: '', code: '', parent_position_id: 'none', planned_budget: 0 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add position');
    }
  };

  const handleDeletePosition = async (pos) => {
    if (!window.confirm(`Delete position '${pos.name}'? Its WBS assignments will also be removed.`)) return;
    try {
      await investmentApi.deletePosition(pos.position_id);
      toast.success('Position deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openAssign = (pos) => {
    setAssignTarget(pos);
    setAssignForm({ wbs_element_id: '', assigned_budget: 0 });
    setAssignOpen(true);
  };

  const handleAssign = async () => {
    if (!assignForm.wbs_element_id) { toast.error('Select a WBS element'); return; }
    try {
      await investmentApi.createAssignment(assignTarget.position_id, {
        wbs_element_id: assignForm.wbs_element_id,
        assigned_budget: parseFloat(assignForm.assigned_budget) || 0,
      });
      toast.success('WBS assigned');
      setAssignOpen(false);
      delete assignmentsByPos[assignTarget.position_id];
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    }
  };

  const toggleExpand = async (positionId) => {
    const next = { ...expanded, [positionId]: !expanded[positionId] };
    setExpanded(next);
    if (next[positionId] && !assignmentsByPos[positionId]) {
      try {
        const res = await investmentApi.listAssignments(positionId);
        setAssignmentsByPos((prev) => ({ ...prev, [positionId]: res.data || [] }));
      } catch (e) { /* noop */ }
    }
  };

  const handleDeleteAssignment = async (positionId, assignmentId) => {
    try {
      await investmentApi.deleteAssignment(assignmentId);
      toast.success('Assignment removed');
      setAssignmentsByPos((prev) => ({ ...prev, [positionId]: undefined }));
      delete assignmentsByPos[positionId];
      await toggleExpand(positionId); await toggleExpand(positionId);
      load();
    } catch (e) {
      toast.error('Failed to remove assignment');
    }
  };

  if (loading) {
    return <Layout><div className="p-6 text-center text-gray-500">Loading…</div></Layout>;
  }
  if (!rollup) {
    return <Layout><div className="p-6 text-center text-gray-500">Program not found.</div></Layout>;
  }

  const positions = rollup.positions || [];

  const kpis = [
    { label: 'Total Budget', value: rollup.total_budget, cls: 'text-stone-800' },
    { label: 'Planned (top-level)', value: rollup.total_planned, cls: 'text-orange-600' },
    { label: 'Assigned to WBS', value: rollup.total_assigned, cls: 'text-blue-600' },
    { label: 'Available', value: rollup.total_available, cls: rollup.total_available < 0 ? 'text-red-600' : 'text-emerald-600' },
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="ip-detail-page">
        <Button variant="ghost" onClick={() => navigate('/ps/investment-programs')} className="mb-3" data-testid="ip-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Back to Programs
        </Button>

        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Landmark className="w-6 h-6 text-orange-600" />
              {rollup.name}
            </h1>
            <div className="text-sm text-gray-500 font-mono mt-1 flex items-center gap-1 flex-wrap">
              {rollup.program_id} · FY {program?.approval_year} · {program?.controlling_area}
              {program?.status && <Badge className="ml-1 bg-stone-100 text-stone-600">{program.status}</Badge>}
            </div>
          </div>
          {writable && (<Button onClick={() => setPosOpen(true)} data-testid="ip-add-position-btn"><Plus className="w-4 h-4 mr-1" />Add Position</Button>)}
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {kpis.map((k) => (
            <Card key={k.label}>
              <CardContent className="pt-5">
                <p className="text-xs text-gray-500">{k.label}</p>
                <p className={`text-xl font-bold ${k.cls}`}>{money(k.value)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader><CardTitle>Budget Roll-up by Position</CardTitle></CardHeader>
          <CardContent>
            {positions.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No positions yet. Click <strong>Add Position</strong> to build the hierarchy.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Position</TableHead>
                    <TableHead className="text-right">Planned</TableHead>
                    <TableHead className="text-right">Children</TableHead>
                    <TableHead className="text-right">Assigned</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positions.map((p) => (
                    <React.Fragment key={p.position_id}>
                      <TableRow data-testid={`ip-pos-${p.code}`} className={p.over_budget ? 'bg-red-50' : ''}>
                        <TableCell style={{ paddingLeft: 12 + p.level * 22 }}>
                          <button
                            className="inline-flex items-center gap-1 text-left"
                            onClick={() => toggleExpand(p.position_id)}
                            data-testid={`ip-expand-${p.code}`}
                          >
                            {p.assignment_count > 0 ? (
                              expanded[p.position_id] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                            ) : <span className="w-4 inline-block" />}
                            <span className="font-medium">{p.name}</span>
                            <span className="font-mono text-xs text-gray-400">{p.code}</span>
                            {p.assignment_count > 0 && <Badge variant="outline" className="ml-1 text-xs">{p.assignment_count} WBS</Badge>}
                          </button>
                        </TableCell>
                        <TableCell className="text-right">{money(p.planned_budget)}</TableCell>
                        <TableCell className="text-right text-gray-500">{money(p.children_planned)}</TableCell>
                        <TableCell className="text-right text-blue-600">{money(p.assigned_budget)}</TableCell>
                        <TableCell className={`text-right font-medium ${p.over_budget ? 'text-red-600' : 'text-emerald-600'}`}>
                          {p.over_budget && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                          {money(p.available)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {writable && (<Button variant="ghost" size="sm" onClick={() => openAssign(p)} data-testid={`ip-assign-${p.code}`}>
                            <Link2 className="w-4 h-4" />
                          </Button>)}
                          {writable && (<Button variant="ghost" size="sm" onClick={() => handleDeletePosition(p)} data-testid={`ip-delpos-${p.code}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>)}
                        </TableCell>
                      </TableRow>
                      {expanded[p.position_id] && (assignmentsByPos[p.position_id] || []).map((a) => (
                        <TableRow key={a.assignment_id} className="bg-stone-50 text-sm">
                          <TableCell style={{ paddingLeft: 40 + p.level * 22 }} className="text-gray-600">
                            <Link2 className="w-3.5 h-3.5 inline mr-1 text-blue-500" />
                            {a.wbs_element_id} — {a.wbs_name || ''}
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                          <TableCell className="text-right text-blue-600">{money(a.assigned_budget)}</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right">
                            {writable && (<Button variant="ghost" size="sm" onClick={() => handleDeleteAssignment(p.position_id, a.assignment_id)} data-testid={`ip-delassign-${a.assignment_id}`}>
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </Button>)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add Position dialog */}
        <Dialog open={posOpen} onOpenChange={setPosOpen}>
          <DialogContent data-testid="ip-position-dialog">
            <DialogHeader><DialogTitle>Add Position</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input value={posForm.name} onChange={(e) => setPosForm({ ...posForm, name: e.target.value })} data-testid="ip-pos-name" />
                </div>
                <div>
                  <Label>Code *</Label>
                  <Input value={posForm.code} onChange={(e) => setPosForm({ ...posForm, code: e.target.value.toUpperCase() })} data-testid="ip-pos-code" />
                </div>
              </div>
              <div>
                <Label>Parent Position</Label>
                <Select value={posForm.parent_position_id} onValueChange={(v) => setPosForm({ ...posForm, parent_position_id: v })}>
                  <SelectTrigger data-testid="ip-pos-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Top level —</SelectItem>
                    {positions.map((p) => (<SelectItem key={p.position_id} value={p.position_id}>{'—'.repeat(p.level)} {p.name} ({p.code})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Planned Budget</Label>
                <Input type="number" value={posForm.planned_budget} onChange={(e) => setPosForm({ ...posForm, planned_budget: e.target.value })} data-testid="ip-pos-budget" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPosOpen(false)}>Cancel</Button>
              <Button onClick={handleAddPosition} data-testid="ip-pos-submit">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign WBS dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent data-testid="ip-assign-dialog">
            <DialogHeader><DialogTitle>Assign WBS to {assignTarget?.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>WBS Element *</Label>
                <Select value={assignForm.wbs_element_id} onValueChange={(v) => setAssignForm({ ...assignForm, wbs_element_id: v })}>
                  <SelectTrigger data-testid="ip-assign-wbs"><SelectValue placeholder="Select WBS element" /></SelectTrigger>
                  <SelectContent>
                    {wbsList.map((w) => (<SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.wbs_element_id} — {w.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assigned Budget</Label>
                <Input type="number" value={assignForm.assigned_budget} onChange={(e) => setAssignForm({ ...assignForm, assigned_budget: e.target.value })} data-testid="ip-assign-budget" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
              <Button onClick={handleAssign} data-testid="ip-assign-submit">Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
